import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { UserService } from './user.service';
import { UnitType, User, CampusUnit } from '../models';
import * as Security from '../tokens/roles.tokens';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Injectable()
export class NavigationService implements OnDestroy {
  byuiUser: User = null;
  proxiedUnit: CampusUnit = null;
  ldsAccount: any = null;

  private userSubscription: Subscription;
  private proxyUnitSubscription: Subscription;

  constructor(private userService: UserService, private router: Router) {
    this.userSubscription = this.userService.user.subscribe(
      user => (this.byuiUser = user)
    );
    this.proxyUnitSubscription = this.userService.proxyUnit.subscribe(
      unit => (this.proxiedUnit = unit)
    );
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.proxyUnitSubscription.unsubscribe();
  }

  /**
   * Tells you if we have a BYU-Idaho Account or LDS Account already logged in.
   */
  alreadyLoggedIn(): boolean {
    return this.byuiUser != null;
  }

  unsubscribeUser() {
    this.byuiUser = null;
  }

  /**
   * Tells you if we have a BYU-Idaho Account or LDS Account already logged in THAT HAS PERMISSIONS TO ACTUALLY DO
   * THINGS IN THE APPLICATION.  You could have someone logged in that can't do anything.
   */
  loggedInAsValidUser(): boolean {
    return (
      this.loggedInAsWard() ||
      this.loggedInAsStake() ||
      this.loggedInAsAdmin() ||
      this.loggedInAsLeader()
    );
  }

  /**
   * Tells you whether or not the user is logged in as an LDS Account for a ward.
   */
  loggedInAsWard(): boolean {
    if (this.proxiedUnit !== null) {
      return this.proxiedUnit.unitType === UnitType.Ward;
    }
    return false;
    // TODO get this from user
  }

  /**
   * Tells you whether or not the user is logged in as an LDS Account for a stake.
   */
  loggedInAsStake(): boolean {
    if (this.proxiedUnit !== null) {
      return this.proxiedUnit.unitType === UnitType.Stake;
    }
    return false;
    // TODO get this from user
  }

  /**
   * Tells you whether or not the user is logged in as BYU-Idaho Administrator.
   */
  loggedInAsAdmin(): boolean {
    return (
      this.byuiUser != null &&
      this.userService.userHasRole([Security.ApplicationRoles.byuiAdmin])
    );
  }

  /**
   * Tells you whether or not the user is proxied in for a stake.
   */
  proxiedForStake(): boolean {
    return this.proxiedUnit && this.proxiedUnit.unitType === UnitType.Stake;
  }

  /**
   * Whether or not the user is proxied for a singles ward or stake.
   */
  proxiedForSinglesUnit(): boolean {
    if (this.proxiedUnit) {
      return !this.proxiedUnit.isForMarriedStudents;
    }
    return false;
  }

  /**
   * Stops proxying for a campus unit.
   */
  stopUnitProxying() {
    this.userService.setProxyStake(null);
    this.userService.setProxyWard(null);
    this.router.navigate(['admin/proxy']);
  }

  /**
   * Tells whether or not user is logged in as BYU-I Leader
   */
  loggedInAsLeader(): boolean {
    return (
      this.byuiUser != null &&
      this.userService.userHasRole([Security.ApplicationRoles.byuiLeader])
    );
  }

  loggedInAsAuthorized(): boolean {
    return this.loggedInAsAdmin() || this.loggedInAsLeader();
  }
}
